/*
<summary>
   This functional component is all about rendering the Add/Edit Tool BOM popup and mapping form data to the API request.
   Developer: Aashish Singh, Created Date: 07-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useStore } from '../../../contexts/store-provider';
import {
  getToolBOMFactoryIdFromLocalStorage,
  getTenantIdFromLocalStorage,
  getUserId,
} from '../../../helpers/localstorage-helper';
import { IAddToolBOM } from '../../../models/forms/IToolBOM';
import { IFormModalProps } from '../../../models/forms/IForm';
import { AddToolBOMInitialState } from '../../../core/initialState';
import AddEditToolBOMForm from '../forms/add-edit-tool-bom-form';

/**
* This functional component is used to render the Add/Edit Tool BOM popup and build the API request from the form values.
* @param props: Component props containing the Tool BOM identifier and the modal close handler
*/
const AddEditToolBOM = (props: IFormModalProps) => {
  const { toolBOMStore } = useStore();
  const {
    AddToolBOMService,
    UpdateToolBOMService,
    GetToolBOMByIdService,
    toolBOMDetail,
  } = toolBOMStore;

  /**
   * This useEffect is used to load the Tool BOM detail when editing, or reset the detail when adding a new one.
   */
  useEffect(() => {
    toolBOMStore.resetDetail();
    if (props.id > 0) {
      GetToolBOMByIdService(props.id);
    }
  }, [props.id]);

  /**
  * This function is used to clear the loaded detail when the popup closes, then close it.
  */
  const onModalClosed = () => {
    toolBOMStore.resetDetail();
    props.modalClosed();
  };

  /**
  * This function is used to build the add/update API request from the form values and submit it to the store.
  * @param values: Add/Edit Tool BOM form values
  */
  const onSubmitForm = (values: IAddToolBOM) => {
    const userId = Number(getUserId()) || 0;
    const factoryId = getToolBOMFactoryIdFromLocalStorage() || '';
    const tenantId = Number(getTenantIdFromLocalStorage());

    if (props.id > 0 && toolBOMStore.getToolBOMByIdState.error) return;

    if (props.id === -1) {
      AddToolBOMService({
        MaterialProductId: values.MaterialProductId,
        ToolModel: values.ToolModel,
        MaterialKatashiki1: values.MaterialKatashiki1,
        Category: values.Category,
        MaterialManufacturerCode: values.MaterialManufacturerCode,
        TecmibizCode: values.TecmibizCode,
        CreatedBy: userId,
        FactoryId: factoryId,
        TenantId: tenantId,
      });
      return;
    }

    UpdateToolBOMService({
      Id: props.id,
      MaterialProductId: values.MaterialProductId,
      ToolModel: values.ToolModel,
      MaterialKatashiki1: values.MaterialKatashiki1,
      Category: values.Category,
      MaterialManufacturerCode: values.MaterialManufacturerCode,
      TecmibizCode: values.TecmibizCode,
      UpdatedBy: userId,
      FactoryId: factoryId,
      TenantId: tenantId,
    });
  };

  return (
    <AddEditToolBOMForm
      initialValues={props.id > 0 ? toolBOMDetail : AddToolBOMInitialState}
      id={props.id}
      submitHandler={onSubmitForm}
      onModalClosed={onModalClosed}
      isLoading={
        toolBOMStore.addUpdateToolBOMState.inProgress ||
        toolBOMStore.getToolBOMByIdState.inProgress
      }
    />
  );
};

export default observer(AddEditToolBOM);
